import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { trackSignup } from '../utils/analytics';

const steps = ['Company Information', 'Admin User Details', 'Confirmation'];

const industries = [
  'Technology',
  'Financial Services',
  'Healthcare',
  'Government',
  'Education',
  'Manufacturing',
  'Retail',
  'Energy',
  'Telecommunications',
  'Other'
];

const companySizes = [
  '1-10',
  '11-50',
  '51-200',
  '201-1000',
  '1000+'
];

const countries = [
  'United States',
  'Canada',
  'United Kingdom',
  'Germany',
  'France',
  'Australia',
  'Japan',
  'Singapore',
  'Other'
];

const CompanyRegister = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    // Company Information
    companyName: '',
    domain: '',
    industry: '',
    size: '',
    country: '',
    phone: '',
    website: '',
    
    // Admin User Details
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminPassword: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      // Company Information validation
      if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
      if (!formData.domain.trim()) newErrors.domain = 'Domain is required';
      if (!formData.industry) newErrors.industry = 'Industry is required';
      if (!formData.size) newErrors.size = 'Company size is required';
      if (!formData.country) newErrors.country = 'Country is required';
      
      // Domain validation
      if (formData.domain && !formData.domain.includes('.')) {
        newErrors.domain = 'Please enter a valid domain (e.g., company.com)';
      }
    }

    if (step === 1) {
      // Admin User validation
      if (!formData.adminFirstName.trim()) newErrors.adminFirstName = 'First name is required';
      if (!formData.adminLastName.trim()) newErrors.adminLastName = 'Last name is required';
      if (!formData.adminEmail.trim()) newErrors.adminEmail = 'Email is required';
      if (!formData.adminPassword) newErrors.adminPassword = 'Password is required';
      if (!formData.confirmPassword) newErrors.confirmPassword = 'Please confirm password';
      
      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (formData.adminEmail && !emailRegex.test(formData.adminEmail)) {
        newErrors.adminEmail = 'Please enter a valid email address';
      }
      
      // Password validation
      if (formData.adminPassword && formData.adminPassword.length < 8) {
        newErrors.adminPassword = 'Password must be at least 8 characters';
      }
      
      if (formData.adminPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(1)) return;

    setLoading(true);
    setError('');

    try {
      const response = await apiService.companies.register({
        companyName: formData.companyName,
        domain: formData.domain,
        industry: formData.industry,
        size: formData.size,
        country: formData.country,
        phone: formData.phone,
        website: formData.website,
        adminFirstName: formData.adminFirstName,
        adminLastName: formData.adminLastName,
        adminEmail: formData.adminEmail,
        adminPassword: formData.adminPassword
      });

      trackSignup({ method: 'email' });
      setSuccess(true);
      setActiveStep(2);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            message: 'Company registered successfully! Please log in.',
            companySlug: response.data.company.slug
          }
        });
      }, 3000);

    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Name"
                value={formData.companyName}
                onChange={handleInputChange('companyName')}
                error={!!errors.companyName}
                helperText={errors.companyName}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Domain"
                value={formData.domain}
                onChange={handleInputChange('domain')}
                error={!!errors.domain}
                helperText={errors.domain || 'e.g., yourcompany.com'}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.industry}>
                <InputLabel>Industry *</InputLabel>
                <Select
                  value={formData.industry}
                  onChange={handleInputChange('industry')}
                  label="Industry *"
                >
                  {industries.map(industry => (
                    <MenuItem key={industry} value={industry}>
                      {industry}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.size}>
                <InputLabel>Company Size *</InputLabel>
                <Select
                  value={formData.size}
                  onChange={handleInputChange('size')}
                  label="Company Size *"
                >
                  {companySizes.map(size => (
                    <MenuItem key={size} value={size}>
                      {size} employees
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.country}>
                <InputLabel>Country *</InputLabel>
                <Select
                  value={formData.country}
                  onChange={handleInputChange('country')}
                  label="Country *"
                >
                  {countries.map(country => (
                    <MenuItem key={country} value={country}>
                      {country}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone}
                onChange={handleInputChange('phone')}
                helperText="Optional"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Website"
                value={formData.website}
                onChange={handleInputChange('website')}
                helperText="Optional - e.g., https://yourcompany.com"
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.adminFirstName}
                onChange={handleInputChange('adminFirstName')}
                error={!!errors.adminFirstName}
                helperText={errors.adminFirstName}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.adminLastName}
                onChange={handleInputChange('adminLastName')}
                error={!!errors.adminLastName}
                helperText={errors.adminLastName}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={formData.adminEmail}
                onChange={handleInputChange('adminEmail')}
                error={!!errors.adminEmail}
                helperText={errors.adminEmail || 'This will be your login email'}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={formData.adminPassword}
                onChange={handleInputChange('adminPassword')}
                error={!!errors.adminPassword}
                helperText={errors.adminPassword || 'Minimum 8 characters'}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={formData.confirmPassword}
                onChange={handleInputChange('confirmPassword')}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                required
              />
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Box textAlign="center">
            {success ? (
              <>
                <CheckCircleIcon color="success" sx={{ fontSize: 80, mb: 2 }} />
                <Typography variant="h4" gutterBottom color="success.main">
                  Registration Successful!
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Your company account has been created successfully. You will be redirected to the login page shortly.
                </Typography>
                <Card sx={{ mt: 3, textAlign: 'left' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      What's Next?
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Your 60-day evaluation has started" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Admin account created and ready to use" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="You can invite team members from the dashboard" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Start creating detections and cases immediately" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </>
            ) : (
              <CircularProgress size={60} />
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            Create Your Company Account
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Get started with your 60-day evaluation - no credit card required
          </Typography>
        </Box>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel
                StepIconComponent={({ active, completed }) => (
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: completed ? 'success.main' : active ? 'primary.main' : 'grey.300',
                      color: 'white'
                    }}
                  >
                    {completed ? <CheckCircleIcon /> : index === 0 ? <BusinessIcon /> : index === 1 ? <PersonIcon /> : <CheckCircleIcon />}
                  </Box>
                )}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 4 }}>
          {renderStepContent(activeStep)}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            onClick={activeStep === 0 ? () => navigate('/') : handleBack}
            startIcon={<ArrowBackIcon />}
            disabled={loading}
          >
            {activeStep === 0 ? 'Back to Home' : 'Back'}
          </Button>

          {activeStep < 2 && (
            <Button
              variant="contained"
              onClick={activeStep === steps.length - 2 ? handleSubmit : handleNext}
              endIcon={activeStep === steps.length - 2 ? null : <ArrowForwardIcon />}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : activeStep === steps.length - 2 ? (
                'Create Account'
              ) : (
                'Next'
              )}
            </Button>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default CompanyRegister;
